import type { ReactNode } from "react";
import { useListContext, useTranslate } from "ra-core";
import { X } from "lucide-react";

import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";

import {
  buildFilterChips,
  type FilterChip,
  type FilterDescriptor,
} from "./activeFilters";

/** A chip for state kept outside the list's filters, like a store flag. */
export type ExtraFilterChip = {
  id: string;
  label: string;
  labelArgs?: Record<string, unknown>;
  content?: ReactNode;
  onRemove: () => void;
};

export type ActiveFilterBarProps = {
  descriptors: readonly FilterDescriptor[];
  extraChips?: readonly ExtraFilterChip[];
  /** Resets state outside the list's filters when "Clear all" runs. */
  onClearAll?: () => void;
};

type RenderedChip = Omit<FilterChip, "remove"> & { onRemove: () => void };

/**
 * The chips for the current list, plus one function that clears them all.
 * Shared by the bar and the mobile filter sheet, so both count and clear the
 * same things.
 */
export const useActiveFilterChips = ({
  descriptors,
  extraChips = [],
  onClearAll,
}: ActiveFilterBarProps) => {
  const { filterValues, setFilters } = useListContext();

  const chips: RenderedChip[] = [
    ...buildFilterChips(filterValues, descriptors).map(
      ({ remove, ...chip }) => ({
        ...chip,
        onRemove: () => setFilters(remove(filterValues ?? {}), []),
      }),
    ),
    ...extraChips,
  ];

  const clearAll = () => {
    setFilters({}, []);
    onClearAll?.();
  };

  // For the mobile filter badge, which sits beside the search box, so a
  // search term is already visible and not counted.
  const count = chips.filter((chip) => !chip.id.startsWith("search:")).length;

  return { chips, clearAll, count };
};

/**
 * "Filtered by" and a chip per active filter, above the list results.
 *
 * The X comes first in each chip. Removing a chip shifts the next one into its
 * place, so its X lands where the cursor already is and repeated clicks clear
 * filters one after another without moving the mouse.
 *
 * The bar always renders, at a fixed minimum height, and says "No filters"
 * when there are none. Collapsing it would move the list up under the cursor
 * the moment the last chip goes.
 */
export const ActiveFilterBar = ({
  className,
  ...props
}: ActiveFilterBarProps & { className?: string }) => {
  const translate = useTranslate();
  const { chips, clearAll } = useActiveFilterChips(props);

  const empty = chips.length === 0;

  return (
    <div
      className={cn("flex flex-wrap items-center gap-1.5 min-h-7", className)}
      data-testid="active-filter-bar"
    >
      <span className="text-sm text-muted-foreground mr-1">
        {translate("crm.filters.filtered_by")}
      </span>
      {empty && (
        <span className="text-sm text-muted-foreground">
          {translate("crm.filters.none")}
        </span>
      )}
      {chips.map((chip) => {
        const label = translate(chip.label, {
          _: chip.label,
          ...chip.labelArgs,
        });
        return (
          <Button
            key={chip.id}
            type="button"
            variant="highlight"
            size="sm"
            className="h-7 gap-1 pl-1.5 pr-2.5 cursor-pointer"
            aria-label={translate("crm.filters.remove", { label })}
            onClick={chip.onRemove}
          >
            <X className="size-3.5" data-slot="chip-remove" />
            {chip.content ?? label}
          </Button>
        );
      })}
      <Button
        type="button"
        variant="ghost"
        size="sm"
        className={cn(
          "h-7 px-2 cursor-pointer text-muted-foreground",
          // Keeps its space with no filters, so nothing shifts.
          empty && "invisible",
        )}
        aria-hidden={empty || undefined}
        tabIndex={empty ? -1 : undefined}
        onClick={clearAll}
      >
        {translate("crm.filters.clear_all")}
      </Button>
    </div>
  );
};
